// 📂 frontend/src/components/BackupManager.tsx
import React, { useState, useEffect } from 'react';
import emailApi from '../services/emailApi';
import ConfirmModal from './ConfirmModal';
import { useConfirm } from '../hooks/useConfirm';

interface Backup {
  filename: string;
  size: number;
  created: string;
  age_days: number;
}

interface BackupSettings {
  enabled: boolean;
  retention_days: number;
  backup_hour: number;
  backup_dir: string;
}

interface BackupManagerProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  theme: any; // ThemeConfig from ThemeContext
  currentTheme: string;
}

const BackupManager: React.FC<BackupManagerProps> = ({ theme, currentTheme }) => {
  const [backups, setBackups] = useState<Backup[]>([]);
  const [settings, setSettings] = useState<BackupSettings>({
    enabled: true,
    retention_days: 7,
    backup_hour: 2,
    backup_dir: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const { confirmState, confirm, cancelConfirm } = useConfirm();

  // Load backups and settings on mount
  useEffect(() => {
    loadBackups();
    loadSettings();
  }, []);

  const loadBackups = async () => {
    try {
      setIsLoading(true);
      const data = await emailApi.getBackups();
      setBackups(data);
    } catch (error: unknown) {
      console.error('Error loading backups:', error);
      setMessage({ type: 'error', text: 'Eroare la conectarea cu serverul' });
    } finally {
      setIsLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      setIsLoadingSettings(true);
      const data = await emailApi.getBackupSettings();
      setSettings(data);
    } catch (error: unknown) {
      console.error('Error loading settings:', error);
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const createBackup = async () => {
    const confirmed = await confirm({
      title: 'Creare Backup',
      message: 'Vrei să creezi un backup manual acum?',
      variant: 'info',
      confirmText: 'Creează',
      cancelText: 'Anulează'
    });
    if (!confirmed) return;

    try {
      setIsCreatingBackup(true);
      setMessage(null);

      await emailApi.createBackup();
      setMessage({ type: 'success', text: 'Backup creat cu succes!' });
      loadBackups(); // Reload list
    } catch (error: unknown) {
      console.error('Error creating backup:', error);
      setMessage({ type: 'error', text: 'Eroare la conectarea cu serverul' });
    } finally {
      setIsCreatingBackup(false);
    }
  };

  const downloadBackup = async (filename: string) => {
    try {
      window.open(emailApi.getBackupDownloadUrl(filename), '_blank');
      setMessage({ type: 'success', text: 'Descărcarea a început...' });
    } catch (error: unknown) {
      console.error('Error downloading backup:', error);
      setMessage({ type: 'error', text: 'Eroare la descărcarea backup-ului' });
    }
  };

  const restoreBackup = async (filename: string) => {
    const confirmed = await confirm({
      title: 'Restaurare Backup',
      message: `ATENȚIE: Această acțiune va înlocui toate datele curente cu cele din backup!\n\nEști sigur că vrei să restaurezi backup-ul ${filename}?\n\nToate datele curente vor fi PIERDUTE.`,
      variant: 'danger',
      confirmText: 'Restaurează',
      cancelText: 'Anulează'
    });
    if (!confirmed) return;

    try {
      setIsLoading(true);
      setMessage(null);

      await emailApi.restoreBackup(filename);
      setMessage({ type: 'success', text: 'Backup restaurat cu succes! Reîncarcă pagina pentru a vedea modificările.' });
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error: unknown) {
      console.error('Error restoring backup:', error);
      setMessage({ type: 'error', text: 'Eroare la conectarea cu serverul' });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteBackup = async (filename: string) => {
    const confirmed = await confirm({
      title: 'Ștergere Backup',
      message: `Ești sigur că vrei să ștergi backup-ul ${filename}?`,
      variant: 'danger',
      confirmText: 'Șterge',
      cancelText: 'Anulează'
    });
    if (!confirmed) return;

    try {
      setIsLoading(true);

      await emailApi.deleteBackup(filename);
      setMessage({ type: 'success', text: 'Backup șters cu succes!' });
      loadBackups();
    } catch (error: unknown) {
      console.error('Error deleting backup:', error);
      setMessage({ type: 'error', text: 'Eroare la conectarea cu serverul' });
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setIsLoadingSettings(true);
      setMessage(null);

      await emailApi.saveBackupSettings(settings);
      setMessage({ type: 'success', text: 'Setări salvate cu succes!' });
      setShowSettings(false);
    } catch (error: unknown) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Eroare la conectarea cu serverul' });
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const formatSize = (bytes: number): string => {
    return `${bytes.toFixed(2)} MB`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('ro-RO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAgeColor = (days: number): string => {
    if (days === 0) return theme.colors.success;
    if (days <= 3) return theme.colors.primary;
    if (days <= 5) return theme.colors.warning;
    return theme.colors.danger;
  };

  const styles = {
    container: {
      padding: '20px',
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.large,
      border: `1px solid ${theme.colors.border}`
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '30px'
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      margin: 0,
      ...(currentTheme === 'futuristic' && { letterSpacing: '0.1em' })
    },
    subtitle: {
      fontSize: '14px',
      color: theme.colors.textSecondary,
      marginTop: '5px'
    },
    button: {
      padding: '10px 20px',
      backgroundColor: theme.colors.primary,
      color: '#ffffff',
      border: 'none',
      borderRadius: theme.borderRadius.medium,
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: theme.transitions.fast,
      marginLeft: '10px'
    },
    secondaryButton: {
      padding: '10px 20px',
      backgroundColor: 'transparent',
      color: theme.colors.primary,
      border: `1px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.medium,
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: theme.transitions.fast
    },
    dangerButton: {
      padding: '8px 16px',
      backgroundColor: theme.colors.danger,
      color: '#ffffff',
      border: 'none',
      borderRadius: theme.borderRadius.small,
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '500',
      transition: theme.transitions.fast
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      marginTop: '20px'
    },
    th: {
      textAlign: 'left' as const,
      padding: '12px',
      borderBottom: `2px solid ${theme.colors.border}`,
      fontWeight: '600',
      color: theme.colors.textSecondary,
      fontSize: '14px'
    },
    td: {
      padding: '12px',
      borderBottom: `1px solid ${theme.colors.border}`,
      fontSize: '14px'
    },
    emptyState: {
      textAlign: 'center' as const,
      padding: '60px 20px',
      color: theme.colors.textSecondary
    },
    message: {
      padding: '12px 16px',
      borderRadius: theme.borderRadius.medium,
      marginBottom: '20px',
      fontSize: '14px',
      fontWeight: '500'
    },
    settingsModal: {
      position: 'fixed' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: theme.colors.surface,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.large,
      padding: '30px',
      boxShadow: theme.shadows.large,
      width: '90%',
      maxWidth: '500px',
      zIndex: 1000
    },
    overlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 999
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500',
      fontSize: '14px'
    },
    input: {
      width: '100%',
      padding: '10px',
      borderRadius: theme.borderRadius.medium,
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.background,
      color: theme.colors.text,
      fontSize: '14px'
    },
    checkbox: {
      marginRight: '8px'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title} className={currentTheme === 'futuristic' ? 'effect-glow' : ''}>
            💾 Backup Manager
          </h2>
          <p style={styles.subtitle}>
            Gestionează backup-urile bazei de date și configurărilor
          </p>
        </div>
        <div>
          <button
            style={styles.secondaryButton}
            onClick={() => setShowSettings(true)}
            disabled={isLoadingSettings}
          >
            ⚙️ Setări
          </button>
          <button
            style={styles.button}
            onClick={createBackup}
            disabled={isCreatingBackup}
            className={currentTheme === 'futuristic' ? 'btn futuristic-btn' : ''}
          >
            {isCreatingBackup ? '⏳ Se creează...' : '➕ Backup Nou'}
          </button>
        </div>
      </div>

      {message && (
        <div
          style={{
            ...styles.message,
            backgroundColor: message.type === 'success' 
              ? `${theme.colors.success}22` 
              : `${theme.colors.danger}22`,
            color: message.type === 'success' 
              ? theme.colors.success 
              : theme.colors.danger,
            border: `1px solid ${message.type === 'success' 
              ? theme.colors.success 
              : theme.colors.danger}`
          }}
        >
          {message.type === 'success' ? '✅' : '❌'} {message.text}
        </div>
      )}

      {isLoading ? (
        <div style={styles.emptyState}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔄</div>
          <h3>Se încarcă backup-urile...</h3>
        </div>
      ) : backups.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
          <h3>Nu există backup-uri încă</h3>
          <p>Creează primul backup pentru a proteja datele tale</p>
        </div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Nume Fișier</th>
              <th style={styles.th}>Dimensiune</th>
              <th style={styles.th}>Data Creării</th>
              <th style={styles.th}>Vechime</th>
              <th style={styles.th}>Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {backups.map((backup) => (
              <tr key={backup.filename}>
                <td style={styles.td}>
                  <strong>{backup.filename}</strong>
                </td>
                <td style={styles.td}>{formatSize(backup.size)}</td>
                <td style={styles.td}>{formatDate(backup.created)}</td>
                <td style={styles.td}>
                  <span style={{ color: getAgeColor(backup.age_days) }}>
                    {backup.age_days === 0 ? 'Azi' : 
                     backup.age_days === 1 ? 'Ieri' : 
                     `${backup.age_days} zile`}
                  </span>
                </td>
                <td style={styles.td}>
                  <button
                    style={{ ...styles.secondaryButton, padding: '6px 12px', marginLeft: 0 }}
                    onClick={() => downloadBackup(backup.filename)}
                    title="Descarcă backup"
                  >
                    ⬇️ Descarcă
                  </button>
                  <button
                    style={{ ...styles.secondaryButton, padding: '6px 12px', marginLeft: '8px' }}
                    onClick={() => restoreBackup(backup.filename)}
                    title="Restaurează backup"
                  >
                    🔄 Restaurează
                  </button>
                  <button
                    style={{ ...styles.dangerButton, marginLeft: '8px' }}
                    onClick={() => deleteBackup(backup.filename)}
                    title="Șterge backup"
                  >
                    🗑️ Șterge
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ConfirmModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
        onConfirm={confirmState.onConfirm}
        onCancel={cancelConfirm}
      />

      {/* Settings Modal */}
      {showSettings && (
        <>
          <div style={styles.overlay} onClick={() => setShowSettings(false)} />
          <div style={styles.settingsModal}>
            <h3 style={{ marginBottom: '20px' }}>⚙️ Setări Backup</h3>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                <input
                  type="checkbox"
                  style={styles.checkbox}
                  checked={settings.enabled}
                  onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                />
                Backup automat activat
              </label>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Ora backup-ului zilnic</label>
              <select
                style={styles.input}
                value={settings.backup_hour}
                onChange={(e) => setSettings({ ...settings, backup_hour: parseInt(e.target.value) })}
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>
                    {i.toString().padStart(2, '0')}:00
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Păstrează backup-uri pentru (zile)</label>
              <input
                type="number"
                style={styles.input}
                value={settings.retention_days}
                onChange={(e) => setSettings({ ...settings, retention_days: parseInt(e.target.value) || 7 })}
                min="1"
                max="365"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Director backup</label>
              <input
                type="text"
                style={styles.input}
                value={settings.backup_dir}
                readOnly
                disabled
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px' }}>
              <button
                style={styles.secondaryButton}
                onClick={() => setShowSettings(false)}
              >
                Anulează
              </button>
              <button
                style={styles.button}
                onClick={saveSettings}
                disabled={isLoadingSettings}
                className={currentTheme === 'futuristic' ? 'btn futuristic-btn' : ''}
              >
                {isLoadingSettings ? 'Se salvează...' : 'Salvează'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BackupManager;